import { useEffect, useMemo, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    InputLabel,
    Chip,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { expertPersonalStatsProxy } from './apiGateway';

type PersonalCheckedAd = {
    id: string;
    adId: string;
    decidedAt: string | null;
    moderationResult: string;
    verdictLabel: string;
    slaViolated?: boolean;
    slaApplicable?: boolean;
};

type PersonalExpertStatsResponse = {
    checkedAdsCount: number;
    selectedMonth?: string;
    isOutstaffRole?: boolean;
    month: {
        label: string;
        start: string;
        end: string;
        checkedAdsCount: number;
    };
    recentCheckedAds: PersonalCheckedAd[];
};

function formatDateTime(value: string | null | undefined) {
    if (!value) {
        return '-';
    }
    return new Date(value).toLocaleString('ru-RU');
}

function formatDate(value: string | null | undefined) {
    if (!value) {
        return '-';
    }
    return new Date(value).toLocaleDateString('ru-RU');
}

function renderSlaBadge(violated?: boolean, applicable?: boolean) {
    if (applicable === false) {
        return <Chip size="small" color="default" variant="outlined" label="Не применяется" />;
    }
    if (violated) {
        return <Chip size="small" color="error" label="Нарушен" />;
    }
    return <Chip size="small" color="success" variant="outlined" label="В норме" />;
}

function toMonthKey(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function buildMonthOptions(monthsCount = 12) {
    const now = new Date();
    return Array.from({ length: monthsCount }, (_, index) => {
        const date = new Date(now.getFullYear(), now.getMonth() - index, 1, 12, 0, 0, 0);
        return {
            value: toMonthKey(date),
            label: date.toLocaleString('ru-RU', { month: 'long', year: 'numeric' })
        };
    });
}

export const ExpertPersonalStatisticsPage = () => {
    const notify = useNotify();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<PersonalExpertStatsResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedMonth, setSelectedMonth] = useState<string>(() => toMonthKey(new Date()));
    const monthOptions = useMemo(() => buildMonthOptions(), []);

    const loadStats = async (month: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await expertPersonalStatsProxy.getStats(month);
            setStats(response as PersonalExpertStatsResponse);
        } catch (loadError) {
            console.error('Failed to load expert personal stats:', loadError);
            setError('Не удалось загрузить статистику');
            notify('Не удалось загрузить статистику', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStats(selectedMonth);
    }, [selectedMonth]);

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Title title="Моя статистика эксперта" />
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography>Загрузка статистики...</Typography>
                </Stack>
            </Box>
        );
    }

    if (error || !stats) {
        return (
            <Box sx={{ p: 2 }}>
                <Title title="Моя статистика эксперта" />
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || 'Не удалось загрузить статистику'}
                </Alert>
                <Button variant="contained" onClick={() => loadStats(selectedMonth)}>
                    Повторить
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Title title="Моя статистика эксперта" />

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2, gap: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Моя статистика эксперта
                </Typography>
                <FormControl size="small" sx={{ width: 180 }}>
                    <InputLabel id="expert-personal-stats-month-label">Период</InputLabel>
                    <Select
                        labelId="expert-personal-stats-month-label"
                        value={selectedMonth}
                        label="Период"
                        onChange={(event) => setSelectedMonth(String(event.target.value))}
                    >
                        {monthOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <Card sx={{ borderRadius: 2, mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Итог за месяц: {stats.month?.label || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Период: {formatDate(stats.month?.start)} - {formatDate(stats.month?.end)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Кол-во проверенных объявлений
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.month?.checkedAdsCount ?? 0}
                    </Typography>
                </CardContent>
            </Card>

            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Последние 30 проверенных объявлений</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID объявления</TableCell>
                                    <TableCell>Дата вердикта</TableCell>
                                    <TableCell>Вердикт</TableCell>
                                    {stats.isOutstaffRole ? (
                                        <TableCell>Нарушение SLA</TableCell>
                                    ) : null}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stats.recentCheckedAds.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={stats.isOutstaffRole ? 4 : 3} align="center">Пусто</TableCell>
                                    </TableRow>
                                ) : stats.recentCheckedAds.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{item.adId || '-'}</TableCell>
                                        <TableCell>{formatDateTime(item.decidedAt)}</TableCell>
                                        <TableCell>{item.verdictLabel || '-'}</TableCell>
                                        {stats.isOutstaffRole ? (
                                            <TableCell>{renderSlaBadge(item.slaViolated, item.slaApplicable)}</TableCell>
                                        ) : null}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
